import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import type { DateRangeSelection } from "./date_range_calendar_controller";
import { i18n } from "../lib/i18n";
import { PremisesCensus, type Flip, type IncomingEvent, type Payload, type Reading } from "../lib/premises_census";
import { Excerpt } from "../lib/premises/excerpt";
import { Markers } from "../lib/premises/markers";
import { NightClock } from "../lib/premises/night_clock";
import { PeopleList } from "../lib/premises/people_list";

// The premises page through the night. The server renders one row per person
// and a JSON island of the night's events; this folds the events in
// `PremisesCensus` and moves a pointer through them as the range input (the
// scrubber's source of truth, so the keyboard works) changes. Each frame
// writes only what the crossed events changed: the cursor, the badge, the
// stat texts and, through `PeopleList` and `Excerpt`, the rows that flipped
// and the latest events. The step path is drawn once.
//
// Broadcast events land as `event` targets; `eventTargetConnected` folds them
// in. A reconnect morphs the page, and `turbo:morph` rebuilds from the island.
const KEY_STEP_SECONDS = 60;
// How close, in pixels, a drag must come to a marker to land on it.
const SNAP_PX = 8;

export default class extends Controller<HTMLElement> {
  static targets = [
    "payload", "person", "event", "people", "range", "chart", "path", "area", "cursor", "dot", "badge", "badgeTime", "badgeEvent", "clip",
    "inside", "insideSub", "insideTitle", "outWithBed", "outSub", "eventsCount", "eventsSub", "peak", "peakSub", "peakTitle",
    "subtitle", "whoTitle", "shown", "query", "clearQuery", "empty", "liveToggle",
    "excerpt", "excerptRow", "excerptEmpty",
  ];

  static values = {
    pageUrl: String,
    excerptSize: { type: Number, default: 7 },
    tickMs: { type: Number, default: 30_000 },
    settleMs: { type: Number, default: 250 },
  };

  declare readonly payloadTarget: HTMLScriptElement;
  declare readonly personTargets: HTMLElement[];
  declare readonly peopleTarget: HTMLElement;
  declare readonly rangeTarget: HTMLInputElement;
  declare readonly chartTarget: HTMLElement;
  declare readonly pathTarget: SVGPathElement;
  declare readonly areaTarget: SVGPathElement;
  declare readonly cursorTarget: SVGLineElement;
  declare readonly dotTarget: SVGCircleElement;
  declare readonly badgeTarget: HTMLElement;
  declare readonly badgeTimeTarget: HTMLElement;
  declare readonly badgeEventTarget: HTMLElement;
  declare readonly insideTarget: HTMLElement;
  declare readonly insideSubTarget: HTMLElement;
  declare readonly insideTitleTarget: HTMLElement;
  declare readonly outWithBedTarget: HTMLElement;
  declare readonly outSubTarget: HTMLElement;
  declare readonly eventsCountTarget: HTMLElement;
  declare readonly eventsSubTarget: HTMLElement;
  declare readonly peakTarget: HTMLElement;
  declare readonly peakSubTarget: HTMLElement;
  declare readonly peakTitleTarget: HTMLElement;
  declare readonly subtitleTarget: HTMLElement;
  declare readonly whoTitleTarget: HTMLElement;
  declare readonly shownTarget: HTMLElement;
  declare readonly queryTarget: HTMLInputElement;
  declare readonly hasQueryTarget: boolean;
  declare readonly clearQueryTarget: HTMLElement;
  declare readonly hasClearQueryTarget: boolean;
  declare readonly emptyTarget: HTMLElement;
  declare readonly liveToggleTarget: HTMLElement;
  declare readonly hasLiveToggleTarget: boolean;
  declare readonly excerptTarget: HTMLElement;
  declare readonly hasExcerptTarget: boolean;
  declare readonly excerptRowTarget: HTMLTemplateElement;
  declare readonly excerptEmptyTarget: HTMLElement;

  declare pageUrlValue: string;
  declare excerptSizeValue: number;
  declare tickMsValue: number;
  declare settleMsValue: number;

  private census!: PremisesCensus;
  private clock!: NightClock;
  private markers!: Markers;
  private people!: PeopleList;
  private excerpt: Excerpt | null = null;
  private live = false;
  private seconds = 0;
  private yMax = 1;
  private arrived = new Set<number>();
  private ticking = false;
  private dragging = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private boundMorph = () => this.rebuild();

  connect() {
    this.build();
    document.addEventListener("turbo:morph", this.boundMorph);
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.boundMorph);
    this.stopTicking();
    if (this.settleTimer) clearTimeout(this.settleTimer);
  }

  // --- targets arriving after connect (broadcasts, morphs) ---

  // Fires for the rows already on the page before connect(), when there is no
  // census yet; build() paints those itself.
  personTargetConnected(row: HTMLElement) {
    if (!this.census) return;
    this.people.add(row);
    this.people.paint([this.census.standingOf(Number(row.dataset.personId))]);
  }

  eventTargetConnected(element: HTMLElement) {
    const raw = element.dataset.event;
    element.remove();
    if (!raw || !this.census) return;
    const event = JSON.parse(raw) as IncomingEvent;
    if (!this.census.ingest(event)) return;
    if (this.live) this.arrived.add(event.id);
    this.yMax = Math.max(this.yMax, this.census.peakCensus);
    this.drawPath();
    if (this.live) this.followNow();
    else this.render(this.census.moveTo(this.seconds));
  }

  // --- the scrubber ---

  scrub() {
    this.leaveLive();
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.seekTo(Number(this.rangeTarget.value));
    });
  }

  settle() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settled(), this.settleMsValue);
  }

  drag(event: PointerEvent) {
    if (event.button !== 0) return;
    this.dragging = true;
    this.chartTarget.setPointerCapture?.(event.pointerId);
    this.leaveLive();
    this.seekTo(this.snapped(this.secondsAt(event.clientX)));
    this.rangeTarget.value = String(this.seconds);
  }

  dragMove(event: PointerEvent) {
    if (!this.dragging) return;
    this.rangeTarget.value = String(this.snapped(this.secondsAt(event.clientX)));
    this.scrub();
  }

  dragEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.settle();
  }

  // On a closed night there is nothing to follow, so the pill is the way back to tonight.
  toggleLive() {
    if (this.census.night.nowSeconds === null) {
      this.visit(new URL(this.pageUrlValue, document.baseURI));
    } else if (this.live) {
      this.leaveLive();
      this.settle();
    } else {
      this.enterLive();
    }
  }

  // A night picked on the shared calendar goes to the server, which opens it at its midpoint.
  pick(event: CustomEvent<DateRangeSelection>) {
    const night = event.detail?.start;
    if (!night || night === this.census.night.on) return;
    const url = new URL(this.pageUrlValue, document.baseURI);
    url.searchParams.set("night_on", night);
    this.visit(url);
  }

  // Another night swaps only the frame this page lives in, leaving the facility shell in place.
  private visit(url: URL) {
    Turbo.visit(url.toString(), { frame: this.element.closest("turbo-frame")?.id });
  }

  filter() {
    this.people.filter((id) => this.census.isInside(id));
    this.people.count(this.census.reading(this.seconds).census);
  }

  clearFilter() {
    this.people.clearQuery();
    this.filter();
  }

  // --- building ---

  private build() {
    const payload = JSON.parse(this.payloadTarget.textContent || "{}") as Payload;
    this.census = new PremisesCensus(payload);
    this.clock = new NightClock(payload.night.startsAt, payload.night.timeZone);
    this.markers = new Markers(payload.night);
    this.people = new PeopleList(this.personTargets, {
      query: this.hasQueryTarget ? this.queryTarget : undefined,
      clearQuery: this.hasClearQueryTarget ? this.clearQueryTarget : undefined,
      shown: this.shownTarget,
      empty: this.emptyTarget,
    }, this.clock);
    this.excerpt = this.hasExcerptTarget
      ? new Excerpt({ list: this.excerptTarget, template: this.excerptRowTarget, empty: this.excerptEmptyTarget }, this.clock, (id) => this.people.personOf(id), this.excerptSizeValue)
      : null;
    this.yMax = Math.max(1, this.census.reading(0).bedHolders, this.census.peakCensus);
    this.live = payload.night.live;
    // The range spans the whole night so its thumb lines up with the chart;
    // seeking clamps to the horizon, so nothing beyond now can be reached.
    this.rangeTarget.min = "0";
    this.rangeTarget.max = String(this.census.night.seconds);
    this.rangeTarget.step = String(KEY_STEP_SECONDS);
    this.drawPath();
    this.seekTo(payload.night.at ?? this.census.horizon);
    this.people.paintAll((id) => this.census.standingOf(id));
    this.people.count(this.census.reading(this.seconds).census);
    this.paintMode();
    if (this.live) this.startTicking();
    this.element.dataset.premisesReady = "";
  }

  private rebuild() {
    this.stopTicking();
    this.build();
  }

  // --- moving ---

  private seekTo(seconds: number) {
    this.seconds = Math.max(0, Math.min(this.census.horizon, Math.round(seconds)));
    if (Number(this.rangeTarget.value) !== this.seconds) this.rangeTarget.value = String(this.seconds);
    this.render(this.census.moveTo(this.seconds));
  }

  private settled() {
    const url = new URL(window.location.href);
    if (this.live) url.searchParams.delete("at");
    else url.searchParams.set("at", this.clock.isoAt(this.seconds));
    window.history.replaceState(window.history.state, "", url.toString());
  }

  private enterLive() {
    if (this.census.night.nowSeconds === null) return;
    this.live = true;
    this.paintMode();
    this.followNow();
    this.startTicking();
    this.settle();
  }

  private leaveLive() {
    if (!this.live) return;
    this.live = false;
    this.stopTicking();
    this.paintMode();
    this.render([]);
  }

  private followNow() {
    const now = Math.max(0, Math.min(this.census.night.seconds, this.clock.elapsed()));
    this.census.night.nowSeconds = now;
    this.rangeTarget.value = String(now);
    this.drawPath();
    this.seekTo(now);
    if (now >= this.census.night.seconds) this.leaveLive();
  }

  private startTicking() {
    this.stopTicking();
    this.timer = setInterval(() => this.followNow(), Math.max(1_000, this.tickMsValue));
  }

  private stopTicking() {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  // --- painting ---

  private render(flips: Flip[]) {
    const reading = this.census.reading(this.seconds);
    this.people.paint(flips);
    this.paintStats(reading);
    this.paintCursor(reading);
    this.people.count(reading.census);
    this.excerpt?.paint(this.census, this.arrived);
  }

  private paintStats(reading: Reading) {
    const time = this.clock.timeAt(this.seconds);
    this.insideTarget.textContent = String(reading.census);
    this.insideSubTarget.textContent = i18n.t("premises.stats.inside_sub", { bed: reading.bedInside, holders: reading.bedHolders, services: reading.servicesInside });
    this.insideTitleTarget.textContent = this.live ? i18n.t("premises.stats.inside_now") : i18n.t("premises.stats.inside_at", { time });
    this.outWithBedTarget.textContent = String(reading.outWithBed);
    this.outSubTarget.textContent = i18n.t("premises.stats.out_sub", { pass: reading.onPassOut, missing: reading.outWithBed - reading.onPassOut });
    this.eventsCountTarget.textContent = String(reading.eventsCount);
    this.eventsSubTarget.textContent = i18n.t("premises.stats.events_sub", { in: reading.ins, out: reading.outs, overrides: reading.overrides });
    this.peakTarget.textContent = String(reading.peak);
    this.peakSubTarget.textContent = i18n.t("premises.stats.peak_sub", { time: this.clock.timeAt(reading.peakAt) });
    this.peakTitleTarget.textContent = this.live ? i18n.t("premises.stats.peak_tonight") : i18n.t("premises.stats.peak_by", { time });
    this.subtitleTarget.textContent = this.live ? i18n.t("premises.subtitle.live", { time }) : i18n.t("premises.subtitle.as_of", { moment: this.clock.dateTimeAt(this.seconds) });
    this.whoTitleTarget.textContent = this.live ? i18n.t("premises.people.who_now") : i18n.t("premises.people.who_at", { time });
    this.badgeTimeTarget.textContent = this.live ? i18n.t("premises.badge.now", { time }) : i18n.t("premises.badge.past", { time });
    this.paintMarker();
  }

  // The keyboard steps a minute at a time, so a marker counts as under the
  // cursor within half a step of it; a drag has already snapped onto it.
  private paintMarker() {
    const marker = this.markers.near(this.seconds, KEY_STEP_SECONDS / 2);
    this.badgeEventTarget.hidden = !marker;
    if (marker) this.badgeEventTarget.querySelector("[data-role=label]")!.textContent = i18n.t("premises.badge.roll_call");
  }

  private paintCursor(reading: Reading) {
    const x = ((this.seconds / this.census.night.seconds) * 1000).toFixed(1);
    const y = (92 - (reading.census / this.yMax) * 84).toFixed(1);
    this.cursorTarget.setAttribute("x1", x);
    this.cursorTarget.setAttribute("x2", x);
    this.dotTarget.setAttribute("cx", x);
    this.dotTarget.setAttribute("cy", y);
    this.badgeTarget.style.left = `${((this.seconds / this.census.night.seconds) * 100).toFixed(2)}%`;
  }

  private paintMode() {
    this.element.dataset.premisesMode = this.live ? "live" : "past";
    if (this.hasLiveToggleTarget) this.liveToggleTarget.setAttribute("aria-pressed", String(this.live));
  }

  private drawPath() {
    const line = this.census.stepPath(this.census.horizon, this.yMax);
    const endX = ((this.census.horizon / this.census.night.seconds) * 1000).toFixed(1);
    this.pathTarget.setAttribute("d", line);
    this.areaTarget.setAttribute("d", `${line} V92 H0 Z`);
    this.areaTarget.dataset.endX = endX;
  }

  private snapped(seconds: number): number {
    const width = this.chartTarget.getBoundingClientRect().width;
    return width > 0 ? this.markers.snap(seconds, (SNAP_PX / width) * this.census.night.seconds) : seconds;
  }

  private secondsAt(clientX: number): number {
    const rect = this.chartTarget.getBoundingClientRect();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return Math.max(0, Math.min(1, ratio)) * this.census.night.seconds;
  }
}
